import { useAgent } from 'agents/react';
import { useAgentChat } from '@cloudflare/ai-chat/react';
import type { FileUIPart, UIMessage } from 'ai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  AnonymousLimitError,
  ChatActionConfirmation,
  ChatDiagram,
  ChatMessage,
  ChatSource,
} from '../types';
import { getStoredAppLocale } from '../i18n/types';
import { getBrowserTimeZone } from '../lib/timezone';
import type { ChatExecutionMode } from '../common/chatExecutionMode';
import { buildServerUrl } from '../services/api';
import { useChatSocketToken } from './useChatSocketToken';
import { useMediaQuotaRefreshOnGeneration } from '../features/media-quota/hooks/useMediaQuotaRefreshOnGeneration';
import {
  isImageGenerationPending,
  stripGeneratedImagePlaceholder,
} from '../utils/generatedImageMessage';
import {
  agentReadyWithTimeout,
  isAbortOrCancelMessage,
  isAnonymousLimitMetadata,
  normalizeAgentHost,
  numberValue,
  stringValue,
} from './chatAgentHelpers';

export type { ChatExecutionMode };

interface UseChatAgentOptions {
  conversationId: string;
  agentPublicId: string;
  sessionId: number | null;
  mode?: ChatExecutionMode | 'fast' | 'smart' | 'premium';
  capability?: 'chat' | 'image_to_text' | 'text_to_image' | 'translation';
  enabled?: boolean;
}

function fileToUIPart(file: File): Promise<FileUIPart> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve({
        type: 'file',
        mediaType: file.type || 'application/octet-stream',
        url: reader.result as string,
        filename: file.name,
        size: file.size,
      } as FileUIPart & { filename: string; size: number });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function useChatAgent({
  conversationId,
  agentPublicId,
  sessionId,
  mode = 'normal',
  capability,
  enabled = true,
}: UseChatAgentOptions) {
  const [isSocketOpen, setIsSocketOpen] = useState(false);
  const [sendError, setSendError] = useState<Error | undefined>(undefined);
  const lastAnonymousLimitEventRef = useRef<string | null>(null);
  const messageTimestampFallbacksRef = useRef<Map<string, Date>>(new Map());
  const shouldConnect =
    enabled && Boolean(conversationId) && Boolean(agentPublicId) && sessionId !== null;
  const serverUrl = import.meta.env.VITE_SERVER_URL || '';
  const agentHost = normalizeAgentHost(serverUrl);
  const agentName = 'AgentDO';
  const { socketToken, refreshSocketToken } = useChatSocketToken(shouldConnect);

  useEffect(() => {
    setIsSocketOpen(false);
    messageTimestampFallbacksRef.current.clear();
  }, [agentPublicId, conversationId, sessionId]);

  const agent = useAgent({
    agent: agentName,
    name: conversationId,
    enabled: shouldConnect,
    onOpen: () => {
      setIsSocketOpen(true);
      if (import.meta.env.DEV) {
        console.log('[useChatAgent] socket open', {
          agent: agentName,
          conversationId,
          agentPublicId,
          sessionId,
        });
      }
    },
    onClose: (event) => {
      setIsSocketOpen(false);
      void refreshSocketToken();
      if (import.meta.env.DEV) {
        console.log('[useChatAgent] socket closed', {
          agent: agentName,
          conversationId,
          agentPublicId,
          sessionId,
          code: event.code,
          reason: event.reason,
        });
      }
    },
    onError: (event) => {
      setIsSocketOpen(false);
      if (import.meta.env.DEV) {
        console.error('[useChatAgent] socket error', {
          agent: agentName,
          conversationId,
          agentPublicId,
          sessionId,
          event,
        });
      }
    },
    onIdentity: (name, agentType) => {
      if (import.meta.env.DEV) {
        console.log('[useChatAgent] socket identified', {
          agent: agentType,
          name,
          sessionId,
        });
      }
    },
    ...(socketToken ? { query: { token: socketToken } } : {}),
    ...(agentHost ? { host: agentHost } : {}),
  });

  useEffect(() => {
    if (!import.meta.env.DEV || !shouldConnect) return;

    console.log('[useChatAgent] connecting', {
      agent: agentName,
      routeAgent: 'agentdo',
      conversationId,
      agentPublicId,
      sessionId,
      host: agentHost || window.location.host,
      route: `/agents/agent-d-o/${conversationId}`,
    });
  }, [agentHost, agentPublicId, conversationId, sessionId, shouldConnect]);

  const {
    messages: agentMessages,
    sendMessage: agentSendMessage,
    addToolOutput,
    clearHistory,
    status: chatStatus,
    error: chatError,
    stop,
  } = useAgentChat<unknown, UIMessage>({
    agent,
    getInitialMessages: null,
    resume: false,
    experimental_throttle: 50,
    onError: (error) => {
      if (import.meta.env.DEV) {
        console.error('[useChatAgent] chat stream failed', error);
      }
    },
  });

  const extractText = useCallback((message: UIMessage): string => {
    const raw = message as unknown as {
      content?: unknown;
      parts?: Array<{ type: string; text?: string; url?: string }>;
    };

    if (typeof raw.content === 'string') {
      return raw.content;
    }

    if (Array.isArray(raw.parts)) {
      return raw.parts
        .map((part) => {
          if (part.type === 'text') return part.text ?? '';
          if (part.type === 'file') return '';
          return '';
        })
        .filter(Boolean)
        .join('\n');
    }

    return '';
  }, []);

  const extractReasoning = useCallback((message: UIMessage): string | null => {
    const raw = message as unknown as {
      parts?: Array<{ type: string; text?: string }>;
    };

    if (!Array.isArray(raw.parts)) return null;

    const reasoning = raw.parts
      .filter((part) => part.type === 'reasoning' && part.text?.trim())
      .map((part) => part.text?.trim() ?? '')
      .join('\n\n')
      .trim();

    return reasoning || null;
  }, []);

  const extractSources = useCallback((message: UIMessage): ChatSource[] => {
    const raw = message as unknown as {
      metadata?: {
        sources?: unknown;
      };
    };

    if (!Array.isArray(raw.metadata?.sources)) {
      return [];
    }

    return raw.metadata.sources
      .map((source): ChatSource | null => {
        if (!source || typeof source !== 'object') return null;
        const item = source as Record<string, unknown>;
        if (typeof item.marker !== 'string' || typeof item.title !== 'string') return null;
        return {
          marker: item.marker,
          title: item.title,
          url: typeof item.url === 'string' ? item.url : undefined,
          section: typeof item.section === 'string' ? item.section : undefined,
          excerpt: typeof item.excerpt === 'string' ? item.excerpt : undefined,
        };
      })
      .filter((source): source is ChatSource => Boolean(source));
  }, []);

  const extractAttachments = useCallback((message: UIMessage): ChatMessage['attachments'] => {
    const raw = message as unknown as {
      parts?: Array<{
        type: string;
        mediaType?: string;
        url?: string;
        filename?: string;
        name?: string;
        size?: number;
      }>;
      metadata?: {
        generatedFiles?: unknown;
      };
    };

    const partAttachments = Array.isArray(raw.parts)
      ? raw.parts
          .filter((part) => part.type === 'file')
          .map((part, index) => ({
            publicId: `local-${message.id}-${index}`,
            fileName: part.filename || part.name || `attachment-${index + 1}`,
            mimeType: part.mediaType || 'application/octet-stream',
            fileSize: part.size || 0,
            fileUrl: part.url,
          }))
      : [];
    const generatedAttachments = Array.isArray(raw.metadata?.generatedFiles)
      ? raw.metadata.generatedFiles
          .map((file, index): NonNullable<ChatMessage['attachments']>[number] | null => {
            if (!file || typeof file !== 'object' || Array.isArray(file)) return null;
            const item = file as Record<string, unknown>;
            const filePublicId = stringValue(item.publicId) || stringValue(item.id);
            const publicId = stringValue(item.attachmentPublicId) || filePublicId;
            const fileName = stringValue(item.fileName) || stringValue(item.name);
            const mimeType =
              stringValue(item.mimeType) ||
              stringValue(item.mime_type) ||
              'application/octet-stream';
            const fileSize = numberValue(item.fileSize) ?? numberValue(item.size) ?? 0;
            const rawDownloadUrl = stringValue(item.downloadUrl) || stringValue(item.download_url);
            const downloadUrl =
              rawDownloadUrl || (filePublicId ? `/v1/files/${filePublicId}/download` : '');

            if (!fileName || !downloadUrl) return null;

            return {
              publicId: publicId || `generated-${message.id}-${index}`,
              fileName,
              mimeType,
              fileSize,
              fileUrl: buildServerUrl(downloadUrl),
            };
          })
          .filter((attachment): attachment is NonNullable<ChatMessage['attachments']>[number] =>
            Boolean(attachment)
          )
      : [];

    return [...partAttachments, ...generatedAttachments];
  }, []);

  const extractGeneratedDiagrams = useCallback((message: UIMessage): ChatDiagram[] => {
    const raw = message as unknown as {
      metadata?: {
        generatedDiagrams?: unknown;
      };
    };

    if (!Array.isArray(raw.metadata?.generatedDiagrams)) return [];

    return raw.metadata.generatedDiagrams
      .map((item, index): ChatDiagram | null => {
        if (!item || typeof item !== 'object' || Array.isArray(item)) return null;
        const record = item as Record<string, unknown>;
        const id = stringValue(record.id) || `generated-diag-${message.id}-${index}`;
        const diagramType =
          stringValue(record.diagramType) || stringValue(record.diagram_type) || 'flowchart';
        const mermaid = stringValue(record.mermaid) || stringValue(record.content);
        const title = stringValue(record.title) || undefined;
        const description = stringValue(record.description) || undefined;

        if (!mermaid) return null;

        return {
          id,
          title,
          diagramType,
          mermaid,
          description,
        };
      })
      .filter((diag): diag is ChatDiagram => Boolean(diag));
  }, []);

  const extractActionConfirmation = useCallback(
    (message: UIMessage): ChatActionConfirmation | undefined => {
      const raw = message as unknown as {
        metadata?: {
          confirmation?: unknown;
          pendingConfirmation?: unknown;
          actionConfirmation?: unknown;
        };
      };

      const conf =
        raw.metadata?.confirmation ||
        raw.metadata?.pendingConfirmation ||
        raw.metadata?.actionConfirmation;
      if (!conf || typeof conf !== 'object' || Array.isArray(conf)) return undefined;

      const item = conf as Record<string, unknown>;
      const toolPublicId = stringValue(item.toolPublicId) || stringValue(item.tool_public_id);
      const toolName = stringValue(item.toolName) || stringValue(item.tool_name) || 'external_tool';
      const id = stringValue(item.id) || `conf-${message.id}`;
      const actionTitle = stringValue(item.actionTitle) || stringValue(item.action_title);
      const summary = stringValue(item.summary);
      const riskLevel = (stringValue(item.riskLevel) ||
        stringValue(item.risk_level) ||
        'write') as ChatActionConfirmation['riskLevel'];
      const endpointUrl = stringValue(item.endpointUrl) || stringValue(item.endpoint_url);
      const parameters = (
        item.parameters && typeof item.parameters === 'object' && !Array.isArray(item.parameters)
          ? item.parameters
          : {}
      ) as Record<string, unknown>;
      const idempotencyKey = stringValue(item.idempotencyKey) || stringValue(item.idempotency_key);
      const status = (stringValue(item.status) || 'pending') as ChatActionConfirmation['status'];

      return {
        id,
        toolPublicId,
        toolName,
        actionTitle,
        summary,
        riskLevel,
        endpointUrl,
        parameters,
        idempotencyKey,
        status,
        messageId: message.id,
      };
    },
    []
  );

  const extractConversationTitle = useCallback((message: UIMessage): string | undefined => {
    const raw = message as unknown as {
      metadata?: {
        conversationTitle?: unknown;
      };
    };

    return typeof raw.metadata?.conversationTitle === 'string'
      ? raw.metadata.conversationTitle.trim() || undefined
      : undefined;
  }, []);

  const extractMessageSessionId = useCallback((message: UIMessage): number | null => {
    const raw = message as unknown as {
      metadata?: {
        sessionId?: unknown;
      };
    };

    return typeof raw.metadata?.sessionId === 'number' ? raw.metadata.sessionId : null;
  }, []);

  const extractMessageConversationId = useCallback((message: UIMessage): string | null => {
    const raw = message as unknown as {
      metadata?: {
        conversationId?: unknown;
      };
    };

    return typeof raw.metadata?.conversationId === 'string' ? raw.metadata.conversationId : null;
  }, []);

  const resolveMessageTimestamp = useCallback((message: UIMessage, index: number): Date => {
    const raw = message as unknown as { createdAt?: string | number | Date };
    if (raw.createdAt) {
      return new Date(raw.createdAt);
    }

    const fallbackKey = `${message.role}:${message.id || index}`;
    const existing = messageTimestampFallbacksRef.current.get(fallbackKey);
    if (existing) {
      return existing;
    }

    const fallback = new Date(Date.now() + index);
    messageTimestampFallbacksRef.current.set(fallbackKey, fallback);
    return fallback;
  }, []);

  const extractAnonymousLimit = useCallback(
    (message: UIMessage): AnonymousLimitError | undefined => {
      const metadata = (message as unknown as { metadata?: unknown }).metadata;
      return isAnonymousLimitMetadata(metadata) ? metadata : undefined;
    },
    []
  );

  const isCurrentConversationMessage = useCallback(
    (message: UIMessage): boolean => {
      const messageSessionId = extractMessageSessionId(message);
      const messageConversationId = extractMessageConversationId(message);
      return messageSessionId === sessionId && messageConversationId === conversationId;
    },
    [conversationId, extractMessageConversationId, extractMessageSessionId, sessionId]
  );

  useEffect(() => {
    if (!shouldConnect) {
      return;
    }

    const latestAnonymousLimitMessage = [...(agentMessages || [])].reverse().find((message) => {
      if (!isCurrentConversationMessage(message)) {
        return false;
      }

      const metadata = (message as unknown as { metadata?: unknown }).metadata;
      const content = extractText(message).trim().toLowerCase();
      return (
        isAnonymousLimitMetadata(metadata) ||
        /^guest .+ limit reached\.?$/i.test(content) ||
        content.includes('guest daily token limit reached')
      );
    });

    if (!latestAnonymousLimitMessage) {
      return;
    }

    const eventKey = latestAnonymousLimitMessage.id;
    if (!eventKey || lastAnonymousLimitEventRef.current === eventKey) {
      return;
    }

    lastAnonymousLimitEventRef.current = eventKey;
    const metadata = (latestAnonymousLimitMessage as unknown as { metadata?: unknown }).metadata;
    const detail = isAnonymousLimitMetadata(metadata)
      ? metadata
      : {
          error: 'ANONYMOUS_LIMIT_EXCEEDED',
          message: extractText(latestAnonymousLimitMessage),
          upgradeUrl: '/signup',
        };

    window.dispatchEvent(
      new CustomEvent('quota:anonymous-limit', {
        detail,
      })
    );
  }, [agentMessages, extractText, isCurrentConversationMessage, shouldConnect]);

  useMediaQuotaRefreshOnGeneration({
    enabled: shouldConnect,
    chatStatus,
    conversationId,
    messages: agentMessages,
    isCurrentConversationMessage,
  });

  const messages: ChatMessage[] = useMemo(() => {
    if (!shouldConnect) return [];

    return (agentMessages || [])
      .filter((msg) => {
        return isCurrentConversationMessage(msg);
      })
      .map((msg, index) => {
        const reasoning = extractReasoning(msg);
        const sources = extractSources(msg);
        const attachments = extractAttachments(msg);
        const content = stripGeneratedImagePlaceholder(extractText(msg), attachments);
        const pendingImage = isImageGenerationPending(msg.metadata, attachments);
        const diagrams = extractGeneratedDiagrams(msg);
        const confirmation = extractActionConfirmation(msg);
        const conversationTitle = extractConversationTitle(msg);
        const anonymousLimit = extractAnonymousLimit(msg);

        return {
          id: msg.id || `${msg.role}-${Date.now()}`,
          sessionId: String(sessionId ?? ''),
          role: msg.role === 'user' ? ('user' as const) : ('agent' as const),
          content,
          reasoning,
          sources,
          attachments,
          diagrams,
          confirmation,
          conversationTitle,
          anonymousLimit,
          pendingImage,
          timestamp: resolveMessageTimestamp(msg, index),
          status: 'sent' as const,
        };
      })
      .filter((msg) => {
        if (msg.role === 'agent' && isAbortOrCancelMessage(msg.content)) {
          return false;
        }

        return (
          msg.content.trim().length > 0 ||
          Boolean(msg.reasoning?.trim()) ||
          Boolean(msg.attachments?.length) ||
          Boolean(msg.diagrams?.length) ||
          Boolean(msg.confirmation) ||
          Boolean(msg.pendingImage)
        );
      });
  }, [
    agentMessages,
    extractActionConfirmation,
    extractAnonymousLimit,
    extractConversationTitle,
    extractAttachments,
    extractGeneratedDiagrams,
    isCurrentConversationMessage,
    extractReasoning,
    extractSources,
    extractText,
    resolveMessageTimestamp,
    sessionId,
    shouldConnect,
  ]);

  const sendMessage = useCallback(
    async (content: string, files?: File[], customMetadata?: Record<string, unknown>) => {
      if (!shouldConnect) {
        return;
      }

      await agentReadyWithTimeout(agent.ready);

      const trimmed = content.trim();
      const fileParts = await Promise.all((files ?? []).map(fileToUIPart));

      if (!trimmed && fileParts.length === 0) {
        return;
      }

      setSendError(undefined);
      void agentSendMessage({
        text: trimmed,
        files: fileParts.length > 0 ? fileParts : undefined,
        metadata: {
          agentPublicId,
          sessionId,
          conversationId,
          mode,
          locale: getStoredAppLocale(),
          timezone: getBrowserTimeZone(),
          ...(capability ? { capability } : {}),
          ...(customMetadata || {}),
        },
      }).catch((error) => {
        const normalizedError = error instanceof Error ? error : new Error(String(error));
        setSendError(normalizedError);
        if (import.meta.env.DEV) {
          console.error('[useChatAgent] send message failed', normalizedError);
        }
      });
    },
    [
      agent.ready,
      agentPublicId,
      agentSendMessage,
      capability,
      conversationId,
      mode,
      sessionId,
      shouldConnect,
    ]
  );

  const isAgentConnected = shouldConnect && isSocketOpen && agent.identified;
  const effectiveChatError = chatError ?? sendError;

  return {
    messages,
    sendMessage: shouldConnect ? sendMessage : async () => {},
    status: isAgentConnected ? 'connected' : 'disconnected',
    isConnected: isAgentConnected,
    isConnecting: shouldConnect && !isSocketOpen,
    error: effectiveChatError,
    addToolResult: addToolOutput,
    clearHistory,
    stop,
    chatStatus,
    chatError: effectiveChatError,
  };
}
